import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { compact, extend, snakeCase } from 'lodash';
import { Observable, of } from 'rxjs';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
  ENTITY_ACCEPT_VERSION_HEADER,
} from '../auth-data/auth-http.service';
import { WindowRefService } from 'i2g-ng-utils';
import { DOC_ACCEPT_VERSION } from '../document-data/document-data.data';
import { ErrorService } from '../error-reporting/error.service';
import { File } from '../files/file.model';
import { FilesService } from '../files/files.service';
import { ISelectOption } from '../form-master/components/select-field/select-field.interface';
import { IResponseInfo } from '../http/http.data';
import { ILocaleLanguage } from '../shared/interfaces/locale-language.interface';
import { ImageService } from '../shared/services/image/image.service';
import { LoggerService } from '../shared/services/logger/logger.service';
import {
  ICurrencyInfo,
  MoneyService,
} from '../shared/services/money/money.service';
import { STATEMENT_ACCEPT_VERSION } from '../statement-data/statement-data.data';
import {
  IPdfInfo,
  IRendererImage,
  IRendererOptions,
  IRendererTemplate,
} from './renderer.interface';
import { catchError, flatMap, map, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EnvironmentsEnum } from '../../../environments/environments.enum';

export const noHeaderImageValue = 'none';
export const DOCUMENT_RENDERER_ACCEPT_VERSION = '3.1.0';
export const STATEMENT_RENDERER_ACCEPT_VERSION = '3.0.0';
export const ACCEPT_HEADER = 'Accept';
export const GUTENBERG_ACCEPT_VERSION_HEADER = 'x-i2g-gutenberg-version';
export const GUTENBERG_ACCEPT_VERSION = '5.7.1';

@Injectable()
export class RendererService {
  private _renderer: any;
  private _urlResponseHeader: { [headerKey: string]: string } = {
    [ACCEPT_HEADER]: 'application/vnd.invoice2go.pdf.url+json',
  };
  private _blobResponseHeader: { [headerKey: string]: string } = {
    [ACCEPT_HEADER]: 'application/pdf',
  };
  private _statementHeaders: { [headerKey: string]: string } = {
    [ENTITY_ACCEPT_VERSION_HEADER]: STATEMENT_ACCEPT_VERSION,
    [ACCEPT_VERSION_HEADER]: STATEMENT_RENDERER_ACCEPT_VERSION,
    [GUTENBERG_ACCEPT_VERSION_HEADER]: GUTENBERG_ACCEPT_VERSION,
  };
  private _documentHeaders: { [headerKey: string]: string } = {
    [ENTITY_ACCEPT_VERSION_HEADER]: DOC_ACCEPT_VERSION,
    [ACCEPT_VERSION_HEADER]: DOCUMENT_RENDERER_ACCEPT_VERSION,
    [GUTENBERG_ACCEPT_VERSION_HEADER]: GUTENBERG_ACCEPT_VERSION,
  };

  constructor(
    windowRefService: WindowRefService,
    private _loggerService: LoggerService,
    private _filesService: FilesService,
    private _errorService: ErrorService,
    private _authHttpService: AuthHttpService,
    private _translateService: TranslateService,
    private _moneyService: MoneyService,
    private _imageService: ImageService
  ) {
    this._renderer = windowRefService.nativeWindow.i2gGutenberg; // included in `scripts` in `.angular-cli.json`

    this._renderer.setAssetBaseDir('.');
  }

  getSupportedCountries(): List<ISelectOption> {
    return List(
      compact(
        this._renderer.getSupportedCountries().map(
          (countryCode: string): ISelectOption => {
            const countryKey = `COUNTRIES.${countryCode}`.toUpperCase();
            const countryName = this._translateService.instant(countryKey);

            if (countryName === countryKey) {
              this._loggerService.log('Missing country name:' + countryCode);

              return {
                key: countryCode.toUpperCase(),
                value: countryCode,
                e2eId: `country-${countryCode.toUpperCase()}`,
              };
            }

            return {
              key: countryName,
              value: countryCode,
              e2eId: `country-${countryCode.toUpperCase()}`,
            };
          }
        )
      ).sort((a: ISelectOption, b: ISelectOption) =>
        this._sortItems(a.key.toUpperCase(), b.key.toUpperCase())
      )
    ) as List<ISelectOption>;
  }

  getSupportedCurrencyCodes(): List<ISelectOption> {
    return List(
      compact(
        this._renderer
          .getSupportedCurrencyCodes()
          .map((currencyCode: string) => {
            const currencyInfo: ICurrencyInfo = this._moneyService.getCurrencyInfo(
              currencyCode
            );

            if (currencyInfo) {
              return {
                key: `${currencyInfo.code}${
                  currencyInfo.symbol ? ' (' + currencyInfo.symbol + ')' : ''
                }`,
                value: currencyInfo.code,
                e2eId: `currency-${currencyCode}`,
              };
            } else {
              this._loggerService.log('Missing currency info:', currencyCode);

              return {
                key: currencyCode,
                value: currencyCode,
                e2eId: `currency-${currencyCode}`,
              };
            }
          })
      ).sort((a: ISelectOption, b: ISelectOption) =>
        this._sortItems(a.key.toUpperCase(), b.key.toUpperCase())
      )
    ) as List<ISelectOption>;
  }

  getSupportedLocales(): List<ILocaleLanguage> {
    return List(
      compact(
        this._renderer.getSupportedLocales().map(
          (locale: string): ILocaleLanguage => {
            const languageKey = `LANGUAGE.${snakeCase(locale).toUpperCase()}`;
            const language = this._translateService.instant(languageKey);

            if (language === languageKey) {
              this._loggerService.log('Missing language name:', locale);

              return {
                language: locale,
                locale: locale,
              };
            }

            return {
              language,
              locale,
            };
          }
        )
      ).sort((a: ILocaleLanguage, b: ILocaleLanguage) =>
        this._sortItems(a.language.toUpperCase(), b.language.toUpperCase())
      )
    ) as List<ILocaleLanguage>;
  }

  getColors(): string[] {
    return this._renderer.getTemplateColors();
  }

  getTemplateNames(locale: string = 'en-US'): List<IRendererTemplate> {
    return List(
      this._renderer.getTemplateNames().map((name) => {
        return {
          key: name,
          displayName: this._renderer.getTemplateDisplayName(name, locale),
          thumbnail: this._renderer.getTemplateThumbnail(name),
        };
      })
    );
  }

  getLogoImages(): List<IRendererImage> {
    return List(
      this._renderer.getLogoNames().map(
        (name: string): IRendererImage => {
          return {
            name,
            url: this._renderer.getLogoURL(name),
          };
        }
      )
    );
  }

  getHeaderImages(): List<IRendererImage> {
    return List(
      this._renderer.getHeaderImageNames().map(
        (name: string): IRendererImage => {
          return {
            name,
            url: this._renderer.getHeaderImageURL(name),
          };
        }
      )
    );
  }

  getWatermarkImages(): List<IRendererImage> {
    return List(
      this._renderer.getWatermarkNames().map(
        (name: string): IRendererImage => {
          return {
            name,
            url: this._renderer.getWatermarkPickerURL(name),
          };
        }
      )
    );
  }

  getLogoDimension(
    templateStyle: string,
    locale: string,
    alignment: string,
    size: string,
    imgWidth: number,
    imgHeight: number
  ): { x: number; y: number; width: number; height: number } {
    const result = this._renderer.getLogoDimension(
      templateStyle,
      locale,
      alignment || 'center',
      size || 'medium',
      imgWidth,
      imgHeight
    );

    if (
      result.x === null ||
      result.y === null ||
      result.width === null ||
      result.height === null
    ) {
      this._errorService.alertSystem(
        new Error('one of the logo dimensions is null'),
        {
          logoDimensions: result,
        }
      );
    }

    return result;
  }

  getLogoProperties(
    templateStyle: string,
    locale: string,
    x: number,
    y: number,
    logoWidth: number,
    logoHeight: number
  ): { alignment: string; size: string } {
    return this._renderer.getLogoProperties(
      templateStyle,
      locale,
      x,
      y,
      logoWidth,
      logoHeight
    );
  }

  getAdditionalImageDimension(
    templateStyle: string,
    locale: string,
    size: string,
    imgWidth: number,
    imgHeight: number
  ): { width: number; height: number } {
    const result = this._renderer.getAdditionalImageDimension(
      templateStyle,
      locale,
      size || 'medium',
      imgWidth,
      imgHeight
    );

    if (result.width === null || result.height === null) {
      this._errorService.alertSystem(
        new Error('one of the additional image dimensions is null'),
        {
          additionalImageDimensions: result,
        }
      );
    }

    return result;
  }

  getAdditionalImageProperties(
    templateStyle: string,
    locale: string,
    imageWidth: number,
    imageHeight: number
  ): { size: string } {
    return this._renderer.getAdditionalImageProperties(
      templateStyle,
      locale,
      imageWidth,
      imageHeight
    );
  }

  getTemplateThumbnail(templateStyle: string): string {
    return this._renderer.getTemplateThumbnail(templateStyle);
  }

  renderStatement(
    containerElement: any,
    statementObj: any,
    cardPaymentsEnabled: boolean,
    paypalEcEnabled: boolean
  ): Promise<any> {
    const options: IRendererOptions = {
      disable_pay_now_link: true,
      card_payments_globally_enabled: cardPaymentsEnabled,
      paypal_ec_globally_enabled: paypalEcEnabled,
      bank_transfer_globally_enabled: false, // always false, statements don't support bank transfer
    };

    try {
      return this._renderer.renderStatement(
        containerElement,
        statementObj,
        (guid) => this.fetchImageForRenderer(guid),
        options
      );
    } catch (error) {
      this._errorService.alertSystem(error, { statement: statementObj });

      return Promise.reject(error);
    }
  }

  renderStatementAsPdf(
    statementObj: any,
    cardPaymentsEnabled: boolean,
    paypalEcEnabled: boolean,
    asBlob: boolean = false
  ): Observable<IPdfInfo | Blob> {
    const options: IRendererOptions = {
      disable_pay_now_link: true,
      card_payments_globally_enabled: cardPaymentsEnabled,
      paypal_ec_globally_enabled: paypalEcEnabled,
      bank_transfer_globally_enabled: false, // always false, statements don't support bank transfer
    };
    const payload = {
      options,
      statement: statementObj,
    };
    const url = '/render_statement';
    const contentType = asBlob ? 'blob' : 'json';
    const headers = {
      ...this._statementHeaders,
      ...(asBlob ? this._blobResponseHeader : this._urlResponseHeader),
    };

    return this._authHttpService
      .post(url, payload, headers, false, contentType)
      .pipe(
        map(({ body, ..._ }: IResponseInfo) => (asBlob ? body : body)),
        take(1)
      );
  }

  renderDocument(
    containerElement: any,
    documentObj: any,
    bankTransferEnabled: boolean,
    cardPaymentsEnabled: boolean,
    paypalEcEnabled: boolean
  ): Promise<any> {
    const options: IRendererOptions = {
      disable_pay_now_link: true,
      bank_transfer_globally_enabled: bankTransferEnabled,
      card_payments_globally_enabled: cardPaymentsEnabled,
      paypal_ec_globally_enabled: paypalEcEnabled,
    };

    try {
      return this._renderer.renderDocument(
        containerElement,
        documentObj,
        (guid) => this.fetchImageForRenderer(guid),
        options
      );
    } catch (error) {
      this._errorService.alertSystem(error, { document: documentObj || {} });

      return Promise.reject(error);
    }
  }

  renderDocumentAsPdf(
    documentObj: any,
    bankTransferEnabled: boolean,
    cardPaymentsEnabled: boolean,
    paypalEcEnabled: boolean,
    asBlob: boolean = false
  ): Observable<IPdfInfo | Blob> {
    const options: IRendererOptions = {
      disable_pay_now_link: true,
      bank_transfer_globally_enabled: bankTransferEnabled,
      card_payments_globally_enabled: cardPaymentsEnabled,
      paypal_ec_globally_enabled: paypalEcEnabled,
    };
    const payload = {
      options,
      document: documentObj,
    };
    const url = '/render_document';
    const contentType = asBlob ? 'blob' : 'json';

    return this._authHttpService
      .post(
        url,
        payload,
        extend(
          {},
          this._documentHeaders,
          asBlob ? this._blobResponseHeader : this._urlResponseHeader
        ),
        false,
        contentType
      )
      .pipe(
        map(({ body, ..._ }: IResponseInfo) => (asBlob ? body : body)),
        take(1)
      );
  }

  sendDocument(
    documentUrl: string,
    documentId: string,
    payload: any
  ): Observable<IResponseInfo> {
    return this._authHttpService.post(
      `${documentUrl}/${documentId}/actions/email`,
      payload,
      this._documentHeaders
    );
  }

  sendStatement(clientId: string, payload: any): Observable<IResponseInfo> {
    return this._authHttpService.post(
      `clients/${clientId}/actions/email_statement`,
      payload,
      this._statementHeaders
    );
  }

  fetchImageForRenderer(
    imageGuid: string
  ): Promise<{ contentType: string; bytes: Uint8Array } | string> {
    // load images in the renderer.
    return new Promise((resolve, reject) => {
      this._filesService
        .getFile(imageGuid)
        .pipe(
          flatMap((file: File) =>
            this._imageService.getImageAsArrayBuffer(file.url)
          ),
          catchError((error) => {
            if (environment.env !== EnvironmentsEnum.production) {
              console.error('renderer image fetch error:', error);
            }

            return of([null, null]);
          })
        )
        .subscribe(([contentType, bytes]: any[]) =>
          resolve(
            contentType === null
              ? // https://stackoverflow.com/questions/9126105/blank-image-encoded-as-data-uri
                'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
              : { contentType, bytes }
          )
        );
    });
  }

  private _sortItems(aKey, bKey): number {
    if (aKey < bKey) {
      return -1;
    } else if (aKey > bKey) {
      return 1;
    } else {
      return 0;
    }
  }
}
